import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import { resetLiveConnection } from '@/api/live';
import {
  clearSession, getRefreshToken, hasStoredSession, onSessionEvent, refreshSession, setSession,
} from '@/api/client';
import { qk } from '@/api/queryKeys';
import type { LoginRequest, RegisterRequest } from '@/api/types';
import { setLanguage, storedLanguage } from '@/i18n';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  login: (body: LoginRequest) => Promise<void>;
  register: (body: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>(() => (hasStoredSession() ? 'loading' : 'anonymous'));

  useEffect(() => {
    let cancelled = false;
    if (hasStoredSession()) {
      void refreshSession().then((ok) => {
        if (!cancelled) setStatus(ok ? 'authenticated' : 'anonymous');
      });
    }
    const off = onSessionEvent((e) => {
      if (e.type === 'logout') {
        resetLiveConnection();
        qc.clear();
        setStatus('anonymous');
      }
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [qc]);

  const afterAuth = useCallback(
    (locale: string) => {
      if (!storedLanguage() && (locale === 'tr' || locale === 'en')) setLanguage(locale);
      setStatus('authenticated');
      void qc.invalidateQueries({ queryKey: qk.me });
    },
    [qc],
  );

  const login = useCallback(
    async (body: LoginRequest) => {
      const res = await authApi.login(body);
      resetLiveConnection();
      qc.clear();
      setSession(res);
      afterAuth(res.user.locale);
    },
    [qc, afterAuth],
  );

  const register = useCallback(
    async (body: RegisterRequest) => {
      const res = await authApi.register(body);
      resetLiveConnection();
      qc.clear();
      setSession(res);
      afterAuth(res.user.locale);
    },
    [qc, afterAuth],
  );

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) await authApi.logout({ refreshToken }).catch(() => undefined);
    clearSession();
    resetLiveConnection();
    qc.clear();
    setStatus('anonymous');
  }, [qc]);

  const value = useMemo(() => ({ status, login, register, logout }), [status, login, register, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
